/**
 * People and companies (SPEC §5.9, D12): a person's `company`, a company's `via` (the agency or
 * vendor it is served through: PMI via Up Reply, Dyson via Contentsquare). People and companies
 * are pages carrying #person / #company as explicit tags.
 */
import { TAG } from './builtins.ts'
import { mergedTarget } from './conflicts.ts'
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import type { Node } from './model.ts'
import { keyForId, type RefIndex } from './refs.ts'

export const PERSON_COMPANY = 'person.company'
export const COMPANY_VIA = 'company.via'
export const ME_FIELD = 'sys:me'

const carries = (g: Graph, n: Node, tag: NodeId): boolean => n.deletedAt === null && n.tags.some((t) => mergedTarget(g, t) === mergedTarget(g, tag))

export const isPerson = (g: Graph, n: Node): boolean => carries(g, n, TAG.person)
export const isCompany = (g: Graph, n: Node): boolean => carries(g, n, TAG.company)

const live = (g: Graph, v: unknown): Node | null => {
  if (typeof v !== 'string') return null
  const n = g.get(mergedTarget(g, v))
  return n && n.deletedAt === null ? n : null
}

/** A person's company, or null. */
export function companyOf(g: Graph, person: Node): Node | null {
  return live(g, person.props[PERSON_COMPANY])
}

/** The companies this one is served through, nearest first (Dyson → Contentsquare → …), loop-safe. */
export function viaChain(g: Graph, id: NodeId): Node[] {
  const out: Node[] = []
  const seen = new Set<NodeId>([id])
  for (let cur = g.get(id); cur; ) {
    const next = live(g, cur.props[COMPANY_VIA])
    if (!next || seen.has(next.id)) break
    out.push(next)
    seen.add(next.id)
    cur = next
  }
  return out
}

const byTitle = (a: Node, b: Node): number => (a.title ?? '').localeCompare(b.title ?? '')

/** Companies served through this one (their `via` is it). */
export function servedThrough(g: Graph, refs: RefIndex, id: NodeId): Node[] {
  return refs
    .referrers(keyForId(id))
    .map((x) => g.must(x))
    .filter((n) => isCompany(g, n) && live(g, n.props[COMPANY_VIA])?.id === id)
    .sort(byTitle)
}

/** People whose company is this one. */
export function peopleAt(g: Graph, refs: RefIndex, id: NodeId): Node[] {
  return refs
    .referrers(keyForId(id))
    .map((x) => g.must(x))
    .filter((n) => isPerson(g, n) && companyOf(g, n)?.id === id)
    .sort(byTitle)
}

/** The person marked "this is me" (`@me` in queries), or null. */
export function meOf(g: Graph, candidates: Iterable<Node>): NodeId | null {
  for (const n of candidates) if (n.deletedAt === null && n.props[ME_FIELD] === true) return n.id
  return null
}
